import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { FreeTrialPeriodLabel } from './FreeTrialPeriodLabel';

interface HeaderProps {
  onMenuClick: () => void;
  onSettingsClick: () => void;
  showAdminBadge?: boolean;
  /**
   * 無料実証の終了日。無料実証の会社のときだけ渡す。
   * 渡されると、サービス名の右に「無料利用期間：○月○日まで（あと○日）」を出す。
   */
  freeTrialEndDate?: { toDate?: () => Date } | Date | null;
  /**
   * 会社名と会社ID。渡されると左上に出す（坂本様ご指摘⑤）。PCは製品名の右、スマホは製品名の代わり。
   * 会社IDは管理画面のときだけ渡す。1つの会社で複数の拠点を登録している場合に
   * 管理者が見分けるためのもので（10/19 打ち合わせ）、社員の方は使わないため。
   */
  companyName?: string;
  corporateCode?: string;
}

export const Header: React.FC<HeaderProps> = ({ 
  onMenuClick, 
  onSettingsClick, 
  showAdminBadge = false,
  freeTrialEndDate,
  companyName,
  corporateCode,
}) => {
  const isDesktop = useIsDesktop();

  // 無料実証の期間は管理画面にだけ出す。投稿する社員の方には操作に関係がない。
  const showFreeTrial = showAdminBadge && Boolean(freeTrialEndDate);
  const showCompany = Boolean(companyName);

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: AppColors.primary,
        boxShadow: 'none',
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ width: '100%', maxWidth: '100%' }}>
        {/* PC版ではメニューボタンを非表示 */}
        {!isDesktop && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuClick}
            sx={{ mr: 2, color: 'black' }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
          {/* 製品名。会社名を出すときも、PCでは左上に残す（坂本様のご希望）。
              スマホは幅が足りず会社名が省略されてしまうため、会社名を優先し、
              製品名はメニューの一番下（SidebarBrand）に移す */}
          {(isDesktop || !showCompany) && (
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 800,
                fontSize: 22,
                letterSpacing: 1.0,
                fontFamily: '"Nunito", "Noto Sans JP", sans-serif',
                color: 'black',
                flexShrink: 0,
              }}
            >
              LiaVoice
            </Typography>
          )}

          {showCompany && (
            <Box
              sx={{
                minWidth: 0,
                // PCは製品名と並ぶので、仕切りの線を入れる
                ...(isDesktop && { pl: 1.5, borderLeft: '1px solid rgba(0, 0, 0, 0.25)' }),
              }}
            >
              {/* 長い会社名は、ほかの表示を押し出さないよう末尾を省略する */}
              <Typography
                noWrap
                title={companyName}
                sx={{ fontSize: isDesktop ? 16 : 15, fontWeight: 700, color: 'black', lineHeight: 1.3 }}
              >
                {companyName}
              </Typography>
              {corporateCode && (
                <Typography noWrap sx={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.7)', lineHeight: 1.3 }}>
                  会社ID：{corporateCode}
                </Typography>
              )}
            </Box>
          )}

          {showFreeTrial && <FreeTrialPeriodLabel endDate={freeTrialEndDate} compact={!isDesktop} />}
        </Box>

        {showAdminBadge && (
          <Box
            sx={{
              mr: 1,
              px: 1,
              py: 0.5,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '4px',
              // 幅が狭くても2行に折れないように
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: 'black',
                fontFamily: '"Noto Sans JP", sans-serif',
              }}
            >
              管理画面
            </Typography>
          </Box>
        )}

        <IconButton
          color="inherit"
          onClick={onSettingsClick}
          sx={{ color: 'black' }}
        >
          <SettingsIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};
